import type { OptionDB } from "@project/emerge";
import { Sides } from "@project/emerge-types";
import { AddableType } from "../enum/AddableType";
import { Alignment } from "../enum/Alignment";
import { HorizontalAlignment } from "../enum/HorizontalAlignment";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { VariableOptionType } from "../enum/VariableOptionType";
import { VerticalAlignment } from "../enum/VerticalAlignment";
import type { AddableGridInfos } from "../interfaces/AddableGridInfos";
import type { DishType } from "../interfaces/DishType";
import type { FootType } from "../interfaces/FootType";
import type { HandleType } from "../interfaces/HandleType";
import type { Position } from "../interfaces/Position";
import type { RunnerType } from "../interfaces/RunnerType";
import type { TrolleyHandleType } from "../interfaces/TrolleyHandleType";
import { getOpposingSide } from "../user_options/Side";
import { createVariableOption } from "./createVariableOption";

export type AddableTypes =
  | HandleType
  | TrolleyHandleType
  | RunnerType
  | FootType
  | DishType;

export const AddableNames: { [key in AddableType]: string } = {
  [AddableType.Foot]: "Fuß",
  [AddableType.Dish]: "Einbauschale",
  [AddableType.Handle]: "Griff",
  [AddableType.Runner]: "Rutschleiste",
  [AddableType.TrolleyHandle]: "Ausziehgriff",
  [AddableType.Cutout]: "Ausschnitt",
};

/** Addables that pick an article from the option database. An Ausschnitt does not. */
export type CatalogAddableType = Exclude<AddableType, AddableType.Cutout>;

export const AddableOptionType: {
  [key in CatalogAddableType]: LTCaseUserOption;
} = {
  [AddableType.Foot]: LTCaseUserOption.FootType,
  [AddableType.Dish]: LTCaseUserOption.DishType,
  [AddableType.Handle]: LTCaseUserOption.HandleType,
  [AddableType.Runner]: LTCaseUserOption.RunnerType,
  [AddableType.TrolleyHandle]: LTCaseUserOption.TrolleyHandleType,
};

export interface AddableInfos {
  addableType: AddableType;
  horizontalPosition: Position;
  orientation: number;
  partSelection?: AddableTypes;
  side: Sides;
  sourceOptionUuid: string;
  verticalPosition: Position;
  /** Stable grid index for horizontal position (0, 1, 2, ...) */
  gridIndexH: number;
  /** Stable grid index for vertical position (0, 1, 2, ...) */
  gridIndexV: number;
  /** Point-symmetric mirror copy on the opposing side (margin sign is flipped in calcPosition). */
  mirrored: boolean;
}

export const addableTypeLookup: Partial<
  Record<VariableOptionType, AddableType>
> = {
  [VariableOptionType.DISHES]: AddableType.Dish,
  [VariableOptionType.FEET]: AddableType.Foot,
  [VariableOptionType.HANDLES]: AddableType.Handle,
  [VariableOptionType.RUNNERS]: AddableType.Runner,
  [VariableOptionType.TROLLEY_HANDLES]: AddableType.TrolleyHandle,
  [VariableOptionType.CUTOUTS]: AddableType.Cutout,
};

export const addableTypeToVariableOptionType: {
  [key in AddableType]: VariableOptionType;
} = {
  [AddableType.Dish]: VariableOptionType.DISHES,
  [AddableType.Foot]: VariableOptionType.FEET,
  [AddableType.Handle]: VariableOptionType.HANDLES,
  [AddableType.Runner]: VariableOptionType.RUNNERS,
  [AddableType.TrolleyHandle]: VariableOptionType.TROLLEY_HANDLES,
  [AddableType.Cutout]: VariableOptionType.CUTOUTS,
};

interface AddableGridProperties {
  horizontalAlignment?: HorizontalAlignment;
  horizontalCount?: number;
  horizontalMargin?: number;
  margin?: number;
  orientation?: number;
  mirror?: boolean;
  pointSymmetry?: boolean;
  /** false = Relativ zum Case (gesamte Außenkontur), true = Relativ zum Korpus */
  relativeToBody?: boolean;
  verticalAlignment?: VerticalAlignment;
  verticalCount?: number;
  verticalMargin?: number;
}

/** The grid fields every addable shares, with their defaults filled in. */
function addableGridDefaults({
  horizontalCount = 1,
  horizontalAlignment = HorizontalAlignment.CENTER,
  horizontalMargin = horizontalAlignment === HorizontalAlignment.CENTER
    ? 0
    : 80,
  mirror = false,
  orientation = 0,
  pointSymmetry = false,
  relativeToBody = false,
  verticalAlignment = VerticalAlignment.CENTER,
  verticalCount = 1,
  verticalMargin = verticalAlignment === VerticalAlignment.CENTER ? 0 : 80,
}: AddableGridProperties) {
  return {
    horizontalAlignment,
    horizontalCount,
    horizontalMargin,
    mirror,
    orientation,
    pointSymmetry,
    relativeToBody,
    verticalAlignment,
    verticalCount,
    verticalMargin,
  };
}

export const createAddableGrid = (
  optionDb: OptionDB,
  type: CatalogAddableType,
  side: Sides,
  part: string,
  grid: AddableGridProperties = {},
): AddableGridInfos => {
  return createVariableOption<AddableGridInfos>(
    addableTypeToVariableOptionType[type],
    {
      addableType: type,
      ...addableGridDefaults(grid),
      partSelection: optionDb.getByName(
        AddableOptionType[type],
        part,
      ) as AddableTypes,
      side,
    },
  );
};

export const createCutoutGrid = (
  side: Sides = Sides.FRONT,
  {
    width = 100,
    height = 60,
    cornerRadius = 8,
    ...grid
  }: AddableGridProperties & {
    width?: number;
    height?: number;
    cornerRadius?: number;
  } = {},
): AddableGridInfos => {
  return createVariableOption<AddableGridInfos>(VariableOptionType.CUTOUTS, {
    addableType: AddableType.Cutout,
    ...addableGridDefaults(grid),
    cornerRadius,
    height,
    side,
    width,
  });
};

export const calcPosition = (
  alignment: Alignment,
  count: number,
  margin: number,
  n: number,
  mirrored: boolean,
): Position => {
  // Ensure count is a number (settings might have string values)
  const numCount = Number(count) || 1;

  let posAlongAxis = 0;
  if (numCount === 1) {
    switch (alignment) {
      case Alignment.START:
        posAlongAxis = mirrored ? 1 : 0;
        break;
      case Alignment.CENTER:
        posAlongAxis = 0.5;
        break;
      case Alignment.END:
        posAlongAxis = mirrored ? 0 : 1;
        break;
    }
  } else {
    posAlongAxis = mirrored ? 1 - n / (numCount - 1) : n / (numCount - 1);
  }
  const result = {
    alignment: numCount > 1 ? Alignment.START : alignment,
    margin:
      numCount === 1 && mirrored && alignment === Alignment.CENTER
        ? -margin
        : margin,
    n: posAlongAxis,
  };
  return result;
};

export const compileAddableGrid = (infos: AddableGridInfos): AddableInfos[] => {
  const {
    addableType,
    horizontalAlignment,
    horizontalCount,
    horizontalMargin,
    mirror,
    orientation,
    partSelection,
    pointSymmetry,
    side,
    uuid,
    verticalAlignment,
    verticalCount,
    verticalMargin,
  } = infos;
  const result: AddableInfos[] = [];
  for (const side2 of mirror ? [side, getOpposingSide(side)] : [side]) {
    const onOppositeSide = side2 !== side;
    // Legacy `compileAddableGrid` (old frontend): one mirror flag for both axes — opposite face
    // flips `calcPosition` only when `pointSymmetry` is true (TOP/BOTTOM etc.). Flat mirror
    // (`mirror` without point symmetry) keeps the same grid slot params on both sides; world
    // position comes from per-rect resolve + `horizontalDir*x + verticalDir*y` in AddableBuildStep.
    const mirrored = onOppositeSide && pointSymmetry === true;
    for (let h = 0; h < horizontalCount; h++) {
      for (let v = 0; v < verticalCount; v++) {
        result.push({
          addableType,
          horizontalPosition: calcPosition(
            horizontalAlignment as unknown as Alignment,
            horizontalCount,
            horizontalMargin,
            h,
            mirrored,
          ),
          orientation,
          partSelection,
          side: side2,
          sourceOptionUuid: uuid,
          verticalPosition: calcPosition(
            verticalAlignment as unknown as Alignment,
            verticalCount,
            verticalMargin,
            v,
            mirrored,
          ),
          gridIndexH: h,
          gridIndexV: v,
          mirrored,
        });
      }
    }
  }
  return result;
};
